import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { Subject } from 'rxjs';
import { UntypedFormControl } from '@angular/forms';
import { takeUntil, throttleTime } from 'rxjs/operators';

type DualListType = 'available' | 'confirmed';
type SortBy = 'id' | 'title' | 'name';
/**
 * @description двойной список с поиском по каждому списку отдельно, для удобного перемещения большого количества элементов
 * @param disabled выключает кнопки, например на время загрузки
 * @param filter включает строку поиска
 * @param withMainListUpload включает кнопки выгрузки
 * @param display параметр который будут отображаться элементы списков, по умолчанию 'name'
 * @param height высота компонента, по умолчанию '300px'
 * @param key ключ по которому определеяется уникальность объекта, по умолчанию 'id'
 * @param destination массив объектов, которые уже выбраны
 * @param source исходный массив объектов, который содержит ВСЕ элементы
 * @param titleLeft описание левого списка
 * @param titleRight описание правого списка

 * @param destinationChange эмиттер срабатывает при перемещении элементов между списками
 * @param selectedItemsChange эмиттер срабатывает при выделении элементов в любом списке
 * @param uploadMainList эмиттер выгружает список элементов из основного исходного списка
 * @param uploadConfirmedList эмиттер выгружает список элементов из списка назначения
 */

@Component({
    selector: 'nekta-dual-list',
    templateUrl: './nekta-dual-list.component.html',
    styleUrls: ['./nekta-dual-list.component.less']
})
export class NektaDualListComponent implements OnInit, OnDestroy, OnChanges {
    @Input() titleLeft: string | null = null;
    @Input() titleRight: string | null = null;
    @Input() sortBy: SortBy = 'id';
    @Input() disabled = false;
    @Input() filter = false;
    @Input() withMainListUpload = false;
    @Input() display = 'name';
    @Input() height = '300px';
    @Input() key = 'id';
    @Input() destination: any = [];
    @Input() source: any = [];

    @Output() destinationChange = new EventEmitter();
    @Output() selectedItemsChange = new EventEmitter();
    @Output() uploadMainList = new EventEmitter();
    @Output() uploadConfirmedList = new EventEmitter();

    public availableFiltered: any[] = [];
    public availableSelected: any[] = [];
    public confirmedFiltered: any[] = [];
    public confirmedSelected: any[] = [];

    public isLoading = false;

    public availableSearch = new UntypedFormControl('');
    public confirmedSearch = new UntypedFormControl('');

    private available: any[] = [];
    private confirmed: any[] = [];

    private destroyed$: Subject<void> = new Subject<void>();
    private availableSorts = {
        'id': this.sortById,
        'title': this.sortByTitle,
        'name': this.sortByName,
    };

    constructor() {}

    ngOnInit(): void {
        this.availableSearch.valueChanges.pipe(takeUntil(this.destroyed$), throttleTime(250)).subscribe((res) => {
            const searchString = res?.trim()?.toLowerCase();
            this.availableFiltered = searchString
                ? this.available.filter((w) => w.name?.toLowerCase()?.includes(searchString))
                : this.available.slice(0);
        });
        this.confirmedSearch.valueChanges.pipe(takeUntil(this.destroyed$), throttleTime(250)).subscribe((res) => {
            const searchString = res?.trim()?.toLowerCase();
            this.confirmedFiltered = searchString
                ? this.confirmed.filter((w) => w.name?.toLowerCase()?.includes(searchString))
                : this.confirmed.slice(0);
        });
    }

    ngOnDestroy(): void {
        this.destroyed$.next();
        this.destroyed$.complete();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (
            changes['destination']?.previousValue !== changes['destination']?.currentValue ||
            changes['source']?.previousValue !== changes['source']?.currentValue ||
            changes['key']?.previousValue !== changes['key']?.currentValue ||
            changes['display']?.previousValue !== changes['display']?.currentValue
        ) {
            this.updateLists();
        }
    }

    addToList() {
        const availableDouble = this.available.slice(0);
        const availableSelectedIds = this.availableSelected.map((w) => w.id);
        this.confirmed.push(...this.availableSelected);
        this.available = availableDouble.filter((w) => !availableSelectedIds.includes(w.id));
        this.updateDestination();
    }

    removeFromList() {
        this.updateSelectedItems();
        const confirmedDouble = this.confirmed.slice(0);
        const confirmedSelectedIds = this.confirmedSelected.map((w) => w.id);
        this.available.push(...this.confirmedSelected);
        this.confirmed = confirmedDouble.filter((w) => !confirmedSelectedIds.includes(w.id));
        this.updateDestination();
    }

    isAllSelected(list: DualListType): boolean {
        return list === 'available'
            ? this.availableFiltered.length === this.availableSelected.length
            : this.confirmedFiltered.length === this.confirmedSelected.length;
    }

    isAnySelected(list: DualListType): boolean {
        return list === 'available' ? !!this.availableSelected.length : !!this.confirmedSelected.length;
    }

    isItemSelected(arr: any[], item: any): boolean {
        return !!arr.find((w) => w.id === item?.id);
    }

    selectItem(list: DualListType, item: any) {
        if (list === 'available') {
            if (this.isItemSelected(this.availableSelected, item)) {
                this.availableSelected = this.availableSelected.filter((w) => w.id !== item.id);
            } else {
                this.availableSelected.push(item);
            }
        } else {
            if (this.isItemSelected(this.confirmedSelected, item)) {
                this.confirmedSelected = this.confirmedSelected.filter((w) => w.id !== item.id);
            } else {
                this.confirmedSelected.push(item);
            }
        }
    }

    selectAll(list: DualListType) {
        if (list === 'available') {
            this.availableSelected = this.availableFiltered.slice(0);
        } else {
            this.confirmedSelected = this.confirmedFiltered.slice(0);
        }
    }

    selectNone(list: DualListType) {
        if (list === 'available') {
            this.availableSelected = [];
        } else {
            this.confirmedSelected = [];
        }
    }

    getResourceIconSrc(id): string {
        const resourceIcons = ['hot-water', 'cold-water', 'electro', 'heat', 'gas', 'substance', 'hot2', 'sewage', 'drink_water'];
        if (id < 1 || id > 9) {
            return '';
        }
        return `/assets/icons/devices/${resourceIcons[id - 1]}.svg`;
    }

    uploadAvailable() {
        const availableIds = this.available.map((w) => w.id);
        const fullAvailable = this.source.filter((w) => availableIds.includes(w[this.key]));
        this.uploadMainList.emit(fullAvailable);
    }

    uploadConfirmed() {
        const confirmedIds = this.confirmedFiltered.map((w) => w.id);
        const fullConfirmed = this.source.filter((w) => confirmedIds.includes(w[this.key]));
        this.uploadConfirmedList.emit(fullConfirmed);
    }

    private sortById(a: any, b: any) {
        return a?.id - b?.id;
    }

    private sortByTitle(a: any, b: any) {
        return a?.title.localeCompare(b?.title);
    }

    private sortByName(a: any, b: any) {
        return a?.name.localeCompare(b?.name);
    }

    private updateDestination() {
        this.isLoading = true;
        const confirmedIds = this.confirmed.map((w) => w.id);
        const delayTimeForSource = Math.ceil(confirmedIds.length / 100);
        setTimeout(() => {
            const fullConfirmed = this.source.filter((w) => confirmedIds.includes(w[this.key]));
            this.isLoading = false;
            this.destinationChange.emit(fullConfirmed);
        }, delayTimeForSource);
    }

    private updateSelectedItems() {
        const confirmedSelectedIds = this.confirmedSelected.map((w) => w.id);
        const fullConfirmedSelected = this.source.filter((w) => confirmedSelectedIds.includes(w[this.key]));
        this.selectedItemsChange.emit(fullConfirmedSelected);
    }

    private updateLists() {
        this.confirmed =
            !this.destination || !Array.isArray(this.destination)
                ? []
                : this.destination.map((w) => ({
                      id: w[this.key],
                      name: w[this.display],
                      resource_type_id: w?.resource_type_id
                  }));
        const confirmedIds = this.confirmed.map((w) => w.id);
        this.available = this.source
            .map((w) => ({
                id: w[this.key],
                name: w[this.display],
                resource_type_id: w?.resource_type_id
            }))
            .filter((z) => !confirmedIds.includes(z.id));
        this.available.sort(this.availableSorts[this.sortBy]);
        this.confirmed.sort(this.availableSorts[this.sortBy]);
        this.availableFiltered = this.available.slice(0);
        this.availableSelected = [];
        this.confirmedFiltered = this.confirmed.slice(0);
        this.confirmedSelected = [];
    }
}
